import React, { useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import useAdminKits from '../hooks/useAdminKits.js';
import KitsTable from '../components/kits/KitsTable.jsx';
import KitModal from '../components/kits/KitModal.jsx';
import LoadingView from '../../shared/components/LoadingView';

export default function KitsPage() {
  useDocumentTitle('Kits – Maintella');

  const { data: kits, loading, error, deleteKit, createKit, updateKit } = useAdminKits();
  const [showModal, setShowModal] = useState(false);
  const [editingKit, setEditingKit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleNewKit = () => {
    setEditingKit(null);
    setFormError(null);
    setShowModal(true);
  };

  const handleEditKit = (kit) => {
    setEditingKit(kit);
    setFormError(null);
    setShowModal(true);
  };

  const handleDeleteKit = async (kit) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete kit "${kit.name}" (${kit.sku})?`
    );
    if (!confirmed) return;

    try {
      await deleteKit(kit.id);
    } catch (err) {
      // Use alert here since deletion is rare and important
      alert(err.message || 'Failed to delete kit');
    }
  };

  const handleSaveKit = async (form) => {
    setSaving(true);
    setFormError(null);
    try {
      if (editingKit) {
        await updateKit(editingKit.id, form);
      } else {
        await createKit(form);
      }
      setShowModal(false);
      setEditingKit(null);
    } catch (err) {
      setFormError(err.message || 'Failed to save kit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ObjectTopBar
        title="Kits"
        primaryBtn={{
          btnText: 'New kit',
          btnIcon: 'bi bi-plus-lg',
          variant: 'primary',
          onClick: handleNewKit,
        }}
      />
      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Spare and upgrade kits</h6>
          <p className="text-muted small mb-0">
            Define reusable kits (SKU, revision, description) that can be linked to workflows
            and used when generating single-use kit codes.
          </p>
        </div>

        {loading && kits.length === 0 ? (
          <LoadingView
            symbolClass="bi-box-seam"
            title="Loading kits"
            subtitle="Fetching spare and upgrade kit definitions."
            className="py-5"
          />
        ) : (
          <>
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                Failed to load kits. Please try refreshing the page.
              </div>
            )}
            <KitsTable
              items={kits}
              onEdit={handleEditKit}
              onDelete={handleDeleteKit}
            />
          </>
        )}
      </div>

      <KitModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingKit(null);
          setFormError(null);
        }}
        kit={editingKit}
        onSave={handleSaveKit}
        saving={saving}
        error={formError}
      />
    </>
  );
}


